﻿import TKCustomElementFactory from '@tk/utilities/tk.custom.element.factory';
import Swiper from 'swiper';
import lightGallery from 'lightgallery';
import { LightGallery } from 'lightgallery/lightgallery';
import { LightGallerySettings } from 'lightgallery/lg-settings';

import 'swiper/swiper.min.css';
import 'swiper/modules/thumbs.min.css';
import 'lightgallery/scss/lightgallery.scss';
import 'lightgallery/scss/lg-thumbnail.scss';
import 'lightgallery/scss/lg-zoom.scss';
import 'lightgallery/scss/lg-video.scss';
import { Thumbs } from 'swiper/modules';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import lgVideo from 'lightgallery/plugins/video';
import lgZoom from 'lightgallery/plugins/zoom';

type LightGalleryItem = NonNullable<LightGallerySettings['dynamicEl']>[number];

export default class TKAssetGallery extends TKCustomElementFactory {
    previewElement?: HTMLElement;
    thumbnailsElement?: HTMLElement;
    previewSwiper?: Swiper;
    thumbnailsSwiper?: Swiper;
    lightGalleryInstance: LightGallery | null = null;
    thumbnailsResizeObserver?: ResizeObserver;
    maxThumbnails: number | 'auto' = 'auto';

    constructor() {
        super();
        this.initializeElements();
    }

    connectedCallback(): void {
        this.validateRequiredElements();
        this.initSwipers();
        this.initLightGallery();
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();

        this.thumbnailsResizeObserver?.disconnect();
        this.thumbnailsResizeObserver = undefined;

        this.previewSwiper?.destroy(true, true);
        this.previewSwiper = undefined;

        this.thumbnailsSwiper?.destroy(true, true);
        this.thumbnailsSwiper = undefined;

        this.lightGalleryInstance?.destroy();
        this.lightGalleryInstance = null;
    }

    protected initializeElements(): void {
        this.previewElement = this.querySelector('[data-tk-asset-preview]') || undefined;
        this.thumbnailsElement = this.querySelector('[data-tk-asset-thumbnails]') || undefined;
    }

    protected validateRequiredElements(): void {
        if (!this.previewElement || !this.thumbnailsElement) {
            throw new Error('Asset Gallery: Missing required elements for preview or thumbnails!');
        }
    }

    protected initSwipers(): void {
        if (!this.previewElement || !this.thumbnailsElement) return;

        this.initThumbnailsSwiper();
        this.initPreviewSwiper();
    }

    protected initThumbnailsSwiper(): void {
        if (!this.thumbnailsElement) return;
        this.thumbnailsSwiper = new Swiper(this.thumbnailsElement, {
            modules: [Thumbs],
            spaceBetween: 16,
            slidesPerView: 'auto',
            freeMode: true,
            watchSlidesProgress: true,
        });
    }

    protected initPreviewSwiper(): void {
        if (!this.previewElement) return;
        this.previewSwiper = new Swiper(this.previewElement, {
            init: false,
            modules: [Thumbs],
            spaceBetween: 16,
            thumbs: {
                swiper: this.thumbnailsSwiper,
                slideThumbActiveClass: 'tk-asset-gallery-item--selected',
            },
        });

        this.previewSwiper.on('afterInit', this.setupThumbnailOverflow.bind(this));
        this.previewSwiper.init();
    }

    protected initLightGallery(): void {
        if (!this.previewElement) return;

        const dynamicEl = this.buildLightGalleryItems();
        if (dynamicEl.length === 0) return;

        this.lightGalleryInstance = lightGallery(this, {
            dynamic: true,
            dynamicEl,
            licenseKey: process.env.LIGHTGALLERY_LICENSE_KEY || '',
            plugins: [lgZoom, lgThumbnail, lgVideo],
            thumbnail: true,
            zoom: true,
            download: false,
        });

        this.pushListener({
            event: 'click',
            element: this.previewElement,
            action: this.openLightbox.bind(this),
        });
    }

    protected openLightbox(): void {
        this.lightGalleryInstance?.openGallery(this.previewSwiper?.activeIndex || 0);
    }

    protected setupThumbnailOverflow(): void {
        if (!this.previewSwiper) return;

        const thumbnails = this.previewSwiper.thumbs.swiper.slides;

        if (this.maxThumbnails === 'auto') {
            this.setupAutoThumbnailOverflow(thumbnails);
        } else if (typeof this.maxThumbnails === 'number') {
            this.setupFixedThumbnailOverflow(thumbnails);
        }
    }

    protected setupAutoThumbnailOverflow(thumbnails: HTMLElement[]): void {
        const container = this.thumbnailsElement;
        if (!container) return;

        this.thumbnailsResizeObserver?.disconnect();
        this.thumbnailsResizeObserver = new ResizeObserver(() => {
            this.handleAutoThumbnailResize(container, thumbnails);
        });

        this.thumbnailsResizeObserver.observe(container);
    }

    protected handleAutoThumbnailResize(container: HTMLElement, thumbnails: HTMLElement[]): void {
        const availableWidth = container.clientWidth;
        let usedWidth = 0;
        let visibleCount = 0;
        const gap = Number(this.thumbnailsSwiper?.originalParams.spaceBetween);

        this.resetThumbnailVisibility(thumbnails);

        thumbnails.forEach((element) => {
            const itemWidth = (element as HTMLElement).offsetWidth;
            const tileWithGap = itemWidth + gap;
            if (usedWidth + (tileWithGap * 2 - gap) <= availableWidth) {
                usedWidth += tileWithGap;
                element.hidden = false;
                visibleCount++;
            } else {
                element.hidden = true;
            }
        });

        const hiddenCount = thumbnails.length - visibleCount;
        this.renderMoreCounter(hiddenCount);
    }

    protected resetThumbnailVisibility(thumbnails: HTMLElement[]): void {
        const existingCounter = this.thumbnailsElement?.querySelector('.tk-asset-gallery-item--more');
        if (existingCounter) existingCounter.remove();
        thumbnails.forEach((element) => (element.hidden = false));
    }

    protected setupFixedThumbnailOverflow(thumbnails: HTMLElement[]): void {
        if (typeof this.maxThumbnails !== 'number') return;
        const maxThumbnails = this.maxThumbnails;
        thumbnails.forEach((element, index) => {
            element.hidden = index >= maxThumbnails;
        });

        const hiddenCount = thumbnails.length - this.maxThumbnails!;
        this.renderMoreCounter(hiddenCount);
    }

    protected renderMoreCounter(hiddenCount: number): void {
        if (hiddenCount <= 1) return;

        const counter = document.createElement('div');
        counter.classList.add('tk-asset-gallery-item', 'tk-asset-gallery-item--more');
        counter.setAttribute('data-count', `+${hiddenCount}`);
        this.thumbnailsSwiper?.wrapperEl.appendChild(counter);

        this.pushListener({
            event: 'click',
            element: counter,
            action: this.openLightbox.bind(this),
        });

        this.thumbnailsSwiper?.update();
    }

    protected buildLightGalleryItems(): NonNullable<LightGallerySettings['dynamicEl']> {
        const previewSlides = Array.from(this.previewElement?.querySelectorAll<HTMLElement>('.swiper-slide') || []);
        const thumbnailSlides = Array.from(
            this.thumbnailsElement?.querySelectorAll<HTMLElement>('.swiper-slide') || [],
        );

        return previewSlides.reduce<NonNullable<LightGallerySettings['dynamicEl']>>((items, slide, index) => {
            const lightGalleryItem = this.createLightGalleryItem(slide, thumbnailSlides[index]);

            if (lightGalleryItem) {
                items.push(lightGalleryItem);
            }

            return items;
        }, []);
    }

    protected createLightGalleryItem(slide: HTMLElement, thumbnailSlide?: HTMLElement): LightGalleryItem | null {
        const image = slide.querySelector<HTMLImageElement>('img');
        const thumbnailImage = thumbnailSlide?.querySelector<HTMLImageElement>('img');
        const subHtml = slide.getAttribute('data-sub-html') || image?.alt || undefined;
        const thumb = thumbnailImage?.currentSrc || thumbnailImage?.src || image?.currentSrc || image?.src;
        const videoUrl = slide.dataset.tkVideoUrl;

        if (videoUrl) {
            return {
                src: this.normalizeVideoUrl(videoUrl),
                poster: image?.currentSrc || image?.src,
                thumb,
                subHtml,
            };
        }

        const src = slide.getAttribute('data-src') || image?.currentSrc || image?.src;
        if (!src) {
            return null;
        }

        return {
            src,
            thumb,
            subHtml,
        };
    }

    protected normalizeVideoUrl(rawUrl: string): string {
        try {
            const url = new URL(rawUrl);

            if (url.hostname.includes('youtube.com') && url.pathname.startsWith('/embed/')) {
                const videoId = url.pathname.split('/').pop();

                if (videoId) {
                    const watchUrl = new URL('https://www.youtube.com/watch');
                    watchUrl.searchParams.set('v', videoId);
                    return watchUrl.toString();
                }
            }

            return url.toString();
        } catch {
            return rawUrl;
        }
    }
}
